import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import { api } from './api';
import { NumberFormatContext } from './numberFormat';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const refreshProfile = useCallback(async () => {
    setProfileLoading(true); setProfileError('');
    try { const next = await api.me(); setProfile(next); return next; }
    catch (e) { setProfile(null); setProfileError(e.message); }
    finally { setProfileLoading(false); }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null)).catch(() => setSession(null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) refreshProfile(); else { setProfile(null); setProfileError(''); }
  }, [session, refreshProfile]);

  return <AuthContext.Provider value={{ session, user: session?.user ?? null, profile, refreshProfile, profileError, profileLoading, loading: session === undefined }}><NumberFormatContext.Provider value={profile?.number_format || 'en-US'}>{children}</NumberFormatContext.Provider></AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export function RequireAuth({ children, admin = false }) {
  const { session, loading, profile, profileError, profileLoading, refreshProfile } = useAuth();
  const location = useLocation();
  if (loading) return <div className="container-x py-24 text-sm text-muted-foreground" data-testid="auth-loading">Checking your session…</div>;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (profileError) return <div className="container-x max-w-xl space-y-4 py-16" data-testid="profile-unavailable"><h1 className="h2">Your workspace is temporarily unavailable</h1><p className="text-sm text-muted-foreground" role="alert" data-testid="profile-error">{profileError}</p><div className="flex gap-3"><button className="btn-primary" onClick={refreshProfile} disabled={profileLoading} data-testid="profile-retry">Retry</button><button className="btn-secondary" onClick={() => supabase.auth.signOut({ scope: 'local' })} data-testid="profile-signout">Sign out</button></div></div>;
  if (!profile) return <div className="container-x py-24 text-sm" data-testid="profile-loading">Loading your workspace…</div>;
  if (profile.deleting && location.pathname !== '/app/settings') return <Navigate to="/app/settings" replace />;
  if (admin && profile && !profile.is_admin) return <Navigate to="/app" replace />;
  return children;
}
